// Day Drawer's own Nutrition tab content — deliberately NOT the shared
// calendar/components/log-cards/LogNutritionCard.js (used as-is by the
// athlete/coach calendar Log view, kept untouched — it wraps everything in a
// `.log-card.log-card--nutrition`, complete with its own colored background
// and header, and only covers Protein/Carbs/Fat/kCal, no Fiber). This drawer
// already has its own header/tabs ("Nutrition" is the tab label itself), so
// wrapping the content in another titled, colored card would be redundant —
// this renders just the macro bars, same `.log-card__nutri-*` classNames
// (dark theme inherited for free) minus the card chrome, plus a Fiber row.
// Labels/units reuse translateMacroLabel/translateMacroUnit (viewTokens.js)
// — the same nutritionPage.progress.macros/macroUnits keys Table/Calendar
// already use, singular/plural exactly as established there (Protein/Fat/
// Fiber singular, Carbs plural) — instead of calendar.cards.nutrition's own
// set, which doesn't have a Fiber key at all. "kcal" is the one exception:
// that shared key is "kCal" (abbreviated, matching Table's column header),
// but this card wants the full word "Calories" — a dedicated dayDrawer key
// instead of changing the shared one out from under Table.
// Builds directly from row.target/row.consumed (already computed once by
// buildDayRows/dayData.js) instead of re-deriving via
// getNutritionTargetsForPlanDay like LogNutritionCard does — DayDrawer.js
// already has that work done, no need to redo it or thread
// nutritionPlans/exercises down here just for that.
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";

import { translateMacroLabel, translateMacroUnit } from "./viewTokens";

const ROWS = [
  { key: "proteins", fill: "protein" },
  { key: "carbs", fill: "carbs" },
  { key: "fats", fill: "fat" },
  { key: "fibers", fill: "fiber" },
  { key: "kcal", fill: "cal" },
];

const DayDrawerNutritionCard = ({ target, consumed }) => {
  const { t } = useTranslation(["athlete-portal", "common"]);

  return (
    <div className="log-card__body">
      {ROWS.map(({ key, fill }) => {
        const isCalories = key === "kcal";
        const label = isCalories
          ? t("nutritionPage.progress.dayDrawer.caloriesLabel")
          : translateMacroLabel(t, key);
        // Grams concatenate with no space ("167g"); calories reads as a
        // separate word with a space ("1952 kcal") — translateMacroUnit's
        // "kcal" value has no leading space of its own, so that's added here.
        const unit = isCalories ? ` ${translateMacroUnit(t, key)}` : translateMacroUnit(t, key);
        const value = consumed[key] ?? 0;
        const goal = target ? target[key] || 0 : 0;
        const roundedValue = Math.round(value);

        if (!target || !goal) {
          return (
            <div key={key} className="log-card__nutri-row">
              <span className="np-drawer__nutri-label">{label}</span>
              <span className="tabular-nums">
                {roundedValue}
                {unit}
              </span>
            </div>
          );
        }

        const roundedGoal = Math.round(goal);
        const remaining = roundedGoal - roundedValue;
        const pct = Math.max(0, Math.min(100, (value / goal) * 100));
        // "167g / 220g (53 left)" / "1952 kcal / 2780 kcal (828 over)" —
        // remaining itself carries no unit suffix, only the two absolute
        // numbers do.
        const progressKey =
          remaining >= 0
            ? "nutritionPage.progress.dayDrawer.macroProgressLeft"
            : "nutritionPage.progress.dayDrawer.macroProgressOver";

        return (
          <div key={key} className="log-card__nutri-item">
            <div className="log-card__nutri-row">
              <span className="np-drawer__nutri-label">{label}</span>
              <span className={`log-card__nutri-left log-card__nutri-left--${fill}`}>
                {t(progressKey, {
                  value: roundedValue,
                  goal: roundedGoal,
                  unit,
                  remaining: Math.abs(remaining),
                })}
              </span>
            </div>
            <div className="log-card__nutri-track">
              <div className={`log-card__nutri-fill log-card__nutri-fill--${fill}`} style={{ width: `${pct}%` }} />
            </div>
          </div>
        );
      })}
    </div>
  );
};

DayDrawerNutritionCard.propTypes = {
  target: PropTypes.object,
  consumed: PropTypes.object.isRequired,
};

export default DayDrawerNutritionCard;
